import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import {
  RefreshCw, AlertTriangle, CheckCircle2, XCircle, MinusCircle, Radio, Clock,
  ArrowDownRight, ArrowUpRight, ListChecks, Database, Send, Activity
} from 'lucide-react';

/**
 * Europcar · Checkout Etiketleme Denetimi
 *
 * Backend: GET /api/europcar-ga4/checkout-audit?days=30|60|90|150
 *          GET /api/europcar-ga4/checkout-audit?only=realtime   (hafif yoklama)
 *
 * NEDEN AYRI SAYFA: Europcar web ekibi Eylül 2026'da "checkout adımları
 * uygulandı" dönüşü yaptı. Ana GA4 panosu (/r/europcar-ga4) 90 günlük bir
 * fotoğraf çizer ve 30 dk önbelleklenir — bir etiketlemenin YAYINDA OLUP
 * OLMADIĞINI o çözünürlükte göremezsin. Bu sayfa tek bir soruya bakar:
 * "istediğimiz dataLayer olayları şu anda geliyor mu?" ve cevabı gün, saat ve
 * son 30 dakika çözünürlüğünde verir.
 *
 * 🔑 Sayfanın omurgası "anlık yoklama". Günlük GA4 tabloları gün kapanana kadar
 *    eksiktir, yani "bugün 0 geldi" tek başına KANIT DEĞİLDİR. Kanıt üçlüdür:
 *    gerçek zamanlı (son 30 dk) + bugün + son 7 gün. Üçü de sayfada yan yana.
 *
 * 🔴 Hüküm ("uygulandı / denendi geri alındı / uygulanmadı") burada HESAPLANMAZ,
 *    backend'den gelir — eşikler orada, tek yerde yazılı. Sayfa yalnız gösterir.
 *
 * Sadece Europcar brand'inde görünür (App.js allowBrandIds + Sidebar
 * restrictedBrands). Bilerek yalnız açık tema.
 */

const API_BASE = (() => {
  const raw = (process.env.REACT_APP_API_URL || 'https://api.glowytics.com').trim();
  return raw.replace(/\/+$/, '').replace(/\/api$/, '');
})();

const nf = (n) => Number(n || 0).toLocaleString('tr-TR');
const pct = (x, d = 0) => (x == null ? '—' : `%${(Number(x) * 100).toFixed(d)}`);
const tl = (n) => `${Number(Math.round(n || 0)).toLocaleString('tr-TR')} TL`;
const tlShort = (n) => {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1e6) return `${(v / 1e6).toLocaleString('tr-TR', { maximumFractionDigits: 1 })}M TL`;
  if (Math.abs(v) >= 1e3) return `${(v / 1e3).toLocaleString('tr-TR', { maximumFractionDigits: 0 })}B TL`;
  return `${Math.round(v)} TL`;
};
/** GA4 tarih anahtarı (YYYYMMDD) → "3 Eyl". */
const AYLAR = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
const dLabel = (d) => (d && d.length === 8 ? `${Number(d.slice(6, 8))} ${AYLAR[Number(d.slice(4, 6)) - 1]}` : '—');
const dLong = (d) => (d && d.length === 8 ? `${Number(d.slice(6, 8))} ${AYLAR[Number(d.slice(4, 6)) - 1]} ${d.slice(0, 4)}` : '—');
const saatFark = (iso) => {
  if (!iso) return '—';
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s} sn önce`;
  if (s < 3600) return `${Math.round(s / 60)} dk önce`;
  return `${Math.round(s / 3600)} sa önce`;
};

/* ---------------------------------------------------------------- parçacıklar */

function Badge({ kind, children }) {
  const map = {
    canli: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    kesildi: 'bg-rose-50 text-rose-700 border-rose-200',
    yok: 'bg-slate-100 text-slate-600 border-slate-200',
    uyari: 'bg-amber-50 text-amber-800 border-amber-200',
    bilgi: 'bg-sky-50 text-sky-700 border-sky-200'
  };
  return (
    <span className={`inline-flex items-center border px-1.5 py-0.5 text-[11px] font-semibold ${map[kind] || map.yok}`}>
      {children}
    </span>
  );
}

function Section({ icon: Icon, eyebrow, title, note, children }) {
  return (
    <section className="mt-10">
      <div className="flex items-start gap-2.5">
        {Icon ? <Icon size={18} className="mt-[3px] shrink-0 text-emerald-700" /> : null}
        <div>
          <h2 className="text-[12px] font-bold uppercase tracking-wider text-emerald-700">{eyebrow}</h2>
          <p className="mt-1 text-[20px] font-bold leading-snug tracking-tight text-slate-900">{title}</p>
          {note ? <p className="mt-1.5 max-w-[92ch] text-[13px] leading-relaxed text-slate-500">{note}</p> : null}
        </div>
      </div>
      <div className="mt-4">{children}</div>
    </section>
  );
}

function Th({ children, right }) {
  return (
    <th className={`border-b border-slate-200 px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-slate-500 ${right ? 'text-right' : 'text-left'}`}>
      {children}
    </th>
  );
}
function Td({ children, right, mono, className = '' }) {
  return (
    <td className={`border-b border-slate-100 px-3 py-2 text-[13px] text-slate-700 ${right ? 'text-right' : ''} ${mono ? 'tabular-nums' : ''} ${className}`}>
      {children}
    </td>
  );
}

/* -------------------------------------------------------------------- hüküm */

const VERDICT = {
  uygulandi: {
    tone: 'emerald', icon: CheckCircle2, baslik: 'Checkout adımları yayında',
    alt: 'İstenen olaylar son 7 gün boyunca kesintisiz geliyor.'
  },
  denendi_geri_alindi: {
    tone: 'rose', icon: XCircle, baslik: 'Checkout adımları YAYINDA DEĞİL',
    alt: 'Olaylar canlıda görüldü, sonra kesildi. Kurulum yapılmış ama ayakta değil.'
  },
  uygulanmadi: {
    tone: 'rose', icon: XCircle, baslik: 'Checkout adımları hiç gelmedi',
    alt: 'İstenen olayların hiçbiri seçili dönemde bir kez bile tetiklenmemiş.'
  }
};

function VerdictCard({ verdict, realtime, today }) {
  const v = VERDICT[verdict.code] || VERDICT.uygulanmadi;
  const Icon = v.icon;
  const tones = {
    rose: 'border-rose-300 bg-rose-50', emerald: 'border-emerald-300 bg-emerald-50'
  };
  const text = { rose: 'text-rose-900', emerald: 'text-emerald-900' };
  const sub = { rose: 'text-rose-700', emerald: 'text-emerald-700' };

  const CHECKOUT = ['add_to_cart', 'begin_checkout', 'add_payment_info', 'view_item', 'select_item'];
  // 🔴 realtime null OLABİLİR (cevap önbellekten geldiyse gerçek zamanlı bölüm
  // bilerek boş döner). O durumda "hiç gelmiyor" DENMEZ — henüz sorulmadı.
  const rtVar = !!(realtime && realtime.rows);
  const rtCheckout = (rtVar ? realtime.rows : []).filter((r) => CHECKOUT.includes(r.name));
  const todayCheckout = (today || []).filter((r) => CHECKOUT.includes(r.name));

  // Kanıt üçlüsü: her biri tek başına yetersiz, üçü birlikte kesin.
  const kanitlar = [
    { k: 'Son 30 dakika',
      v: !rtVar ? 'soruluyor…' : (rtCheckout.length ? `${nf(rtCheckout.reduce((a, r) => a + r.count, 0))} checkout olayı` : 'hiç checkout olayı yok'),
      ok: rtCheckout.length > 0, bilinmiyor: !rtVar },
    { k: 'Bugün', v: todayCheckout.length ? `${nf(todayCheckout.reduce((a, r) => a + r.count, 0))} olay` : 'hiç yok', ok: todayCheckout.length > 0 },
    { k: 'Son 7 gün', v: `${verdict.canli}/${verdict.toplam} olay canlı`, ok: verdict.canli > 0 },
    { k: 'Görüldüğü gün sayısı', v: verdict.spikeCount ? `${verdict.spikeCount} gün (${verdict.spikeDates.map(dLabel).join(', ')})` : 'hiç', ok: verdict.spikeCount > 3 }
  ];

  return (
    <div className={`mt-6 border ${tones[v.tone]}`}>
      <div className="flex flex-wrap items-start gap-3 px-5 py-4">
        <Icon size={26} className={`mt-0.5 shrink-0 ${sub[v.tone]}`} />
        <div className="min-w-[280px] flex-1">
          <div className={`text-[22px] font-bold leading-tight tracking-tight ${text[v.tone]}`}>{v.baslik}</div>
          <div className={`mt-1 text-[13px] leading-relaxed ${sub[v.tone]}`}>{v.alt}</div>
        </div>
      </div>
      <div className="grid grid-cols-1 border-t border-white/60 sm:grid-cols-2 lg:grid-cols-4">
        {kanitlar.map((x) => (
          <div key={x.k} className="border-t border-white/60 px-5 py-3 first:border-t-0 sm:border-l sm:border-t-0 sm:first:border-l-0">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{x.k}</div>
            <div className={`mt-0.5 flex items-center gap-1.5 text-[14px] font-bold ${x.bilinmiyor ? 'text-slate-400' : x.ok ? 'text-emerald-700' : 'text-rose-700'}`}>
              {x.bilinmiyor ? <MinusCircle size={14} /> : x.ok ? <CheckCircle2 size={14} /> : <XCircle size={14} />}{x.v}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------- anlık yoklama */

/**
 * Canlı GA4 yoklaması. 60 saniyede bir `only=realtime` ucuna gider (tam denetim
 * ~20 rapor harcıyor, bu tek çağrı ~0,5 sn). Aranan olay listesi sabit: gelmesi
 * İSTENEN olaylar. Gelmeyeni de gösterir — boş satır burada bilginin kendisi.
 */
const ARANAN = [
  'view_item_list', 'view_item', 'select_item', 'add_to_cart',
  'begin_checkout', 'add_payment_info', 'purchase', 'search', 'no_availability'
];

function LiveProbe({ realtime, onPoll, polling, auto, setAuto }) {
  const bekliyor = !(realtime && realtime.rows);
  const rows = (realtime && realtime.rows) || [];
  const map = rows.reduce((a, r) => ({ ...a, [r.name]: r.count }), {});
  const diger = rows.filter((r) => !ARANAN.includes(r.name));

  return (
    <div className="border border-slate-200 bg-white">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-4 py-2.5">
        <div className="flex items-center gap-2">
          <span className="relative flex h-2 w-2">
            <span className={`absolute inline-flex h-full w-full rounded-full bg-emerald-400 ${polling ? 'animate-ping' : ''} opacity-75`} />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
          </span>
          <span className="text-[13px] font-bold text-slate-900">GA4 gerçek zamanlı · son 30 dakika</span>
          <span className="text-[12px] text-slate-500">
            {realtime && realtime.at ? `güncellendi ${saatFark(realtime.at)}` : '—'}
            {realtime ? ` · toplam ${nf(realtime.total)} olay` : ''}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex cursor-pointer select-none items-center gap-1.5 text-[12px] text-slate-600">
            <input type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} className="h-3.5 w-3.5 accent-emerald-600" />
            60 sn'de bir yenile
          </label>
          <button
            onClick={onPoll} disabled={polling}
            className="inline-flex items-center gap-1.5 border border-slate-300 bg-white px-2.5 py-1 text-[12px] font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            <RefreshCw size={12} className={polling ? 'animate-spin' : ''} />Şimdi sor
          </button>
        </div>
      </div>

      {realtime && realtime.error ? (
        <div className="px-4 py-3 text-[13px] text-amber-800">Gerçek zamanlı rapor alınamadı: {realtime.error}</div>
      ) : null}

      <div className="grid grid-cols-1 sm:grid-cols-3">
        {ARANAN.map((e) => {
          const n = map[e] || 0;
          return (
            <div key={e} className={`flex items-center justify-between gap-2 border-b border-r border-slate-100 px-4 py-2 ${n ? '' : 'bg-slate-50/60'}`}>
              <span className={`font-mono text-[12px] ${n ? 'text-slate-900' : 'text-slate-400'}`}>{e}</span>
              <span className={`text-[13px] font-bold tabular-nums ${n ? 'text-emerald-700' : 'text-slate-300'}`}>
                {bekliyor ? '…' : n ? nf(n) : '—'}
              </span>
            </div>
          );
        })}
      </div>
      {diger.length ? (
        <div className="px-4 py-2.5 text-[12px] leading-relaxed text-slate-500">
          <span className="font-semibold text-slate-600">Şu an gelen diğer olaylar: </span>
          {diger.map((r) => `${r.name} (${nf(r.count)})`).join(' · ')}
        </div>
      ) : null}
    </div>
  );
}

/* --------------------------------------------------------------- günlük seri */

/**
 * Olay × gün ısı şeridi. Amaç büyüklük kıyaslamak değil, SÜREKLİLİK göstermek:
 * hangi olay hangi gün var. Bu yüzden her satır kendi içinde ölçeklenir ve
 * sıfır gün boş kutu olarak durur — gizlenmez.
 */
function HeatStrip({ dates, series, names, highlight }) {
  return (
    <div className="overflow-x-auto border border-slate-200 bg-white">
      <table className="w-full border-collapse">
        <tbody>
          {names.map((name) => {
            const row = series[name] || {};
            const max = Math.max(...dates.map((d) => row[d] || 0), 1);
            const aktif = dates.filter((d) => (row[d] || 0) > 0).length;
            return (
              <tr key={name} className="border-b border-slate-100 last:border-b-0">
                <td className="whitespace-nowrap px-3 py-1.5 font-mono text-[12px] text-slate-700">{name}</td>
                <td className="whitespace-nowrap px-2 py-1.5 text-right text-[11px] tabular-nums text-slate-400">
                  {aktif}/{dates.length} gün
                </td>
                <td className="px-2 py-1.5">
                  <div className="flex items-end gap-[1px]">
                    {dates.map((d) => {
                      const v = row[d] || 0;
                      const h = v ? Math.max(3, Math.round((v / max) * 22)) : 0;
                      const vurgu = highlight && highlight.includes(d);
                      return (
                        <div
                          key={d}
                          title={`${dLong(d)} · ${name}: ${nf(v)}`}
                          className="flex h-[24px] w-[6px] shrink-0 items-end"
                        >
                          {v
                            ? <div className={`w-full ${vurgu ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ height: `${h}px` }} />
                            : <div className={`h-[2px] w-full ${vurgu ? 'bg-amber-200' : 'bg-slate-200'}`} />}
                        </div>
                      );
                    })}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ------------------------------------------------------------ saat kırılımı */

function HourlyChart({ hourly }) {
  if (!hourly || !hourly.rows || !hourly.rows.length) return null;
  const EVS = [
    { k: 'add_to_cart', c: 'bg-emerald-500', l: 'add_to_cart' },
    { k: 'begin_checkout', c: 'bg-sky-500', l: 'begin_checkout' },
    { k: 'add_payment_info', c: 'bg-violet-500', l: 'add_payment_info' },
    { k: 'purchase', c: 'bg-slate-400', l: 'purchase' }
  ];
  const rows = Array.from({ length: 24 }, (_, h) => hourly.rows.find((r) => r.hour === h) || { hour: h, events: {} });
  const max = Math.max(...rows.map((r) => Math.max(...EVS.map((e) => r.events[e.k] || 0))), 1);

  return (
    <div className="border border-slate-200 bg-white p-4">
      <div className="mb-3 flex flex-wrap items-center gap-3">
        {EVS.map((e) => (
          <span key={e.k} className="flex items-center gap-1.5 text-[12px] text-slate-600">
            <span className={`h-2.5 w-2.5 ${e.c}`} />{e.l}
          </span>
        ))}
      </div>
      <div className="overflow-x-auto">
        <div className="flex min-w-[720px] items-end gap-[3px]">
          {rows.map((r) => (
            <div key={r.hour} className="flex flex-1 flex-col items-center gap-1">
              <div className="flex h-[120px] w-full items-end justify-center gap-[2px]">
                {EVS.map((e) => {
                  const v = r.events[e.k] || 0;
                  return (
                    <div
                      key={e.k}
                      title={`${String(r.hour).padStart(2, '0')}:00 · ${e.l}: ${nf(v)}`}
                      className={`w-full ${v ? e.c : 'bg-slate-100'}`}
                      style={{ height: v ? `${Math.max(2, (v / max) * 120)}px` : '2px' }}
                    />
                  );
                })}
              </div>
              <div className="text-[10px] tabular-nums text-slate-400">{String(r.hour).padStart(2, '0')}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------- doluluk zaman serisi */

/**
 * Bir alanın gün gün doluluk oranı. Kazanç yeşil, kayıp kırmızı çizilir; kırılma
 * günü dikey çizgiyle işaretlenir. "Ne zaman oldu" sorusunun cevabı bu grafik.
 */
function FillChart({ daily, col, tone, breakDate }) {
  const W = 960, H = 64;
  const pts = daily.map((r, i) => ({ i, d: r.date, v: r[col] }));
  const n = Math.max(1, pts.length - 1);
  const x = (i) => (i / n) * W;
  const y = (v) => H - v * (H - 6) - 3;
  const segs = [];
  let cur = [];
  pts.forEach((p) => {
    if (p.v == null) { if (cur.length) segs.push(cur); cur = []; return; }
    cur.push(`${x(p.i).toFixed(1)},${y(p.v).toFixed(1)}`);
  });
  if (cur.length) segs.push(cur);
  const stroke = tone === 'kayip' ? '#e11d48' : '#059669';
  const bIdx = breakDate ? pts.findIndex((p) => p.d === breakDate) : -1;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="h-[64px] w-full" preserveAspectRatio="none">
      <line x1="0" y1={y(1)} x2={W} y2={y(1)} stroke="#e2e8f0" strokeWidth="1" strokeDasharray="3 3" />
      <line x1="0" y1={y(0)} x2={W} y2={y(0)} stroke="#e2e8f0" strokeWidth="1" />
      {bIdx >= 0 ? <line x1={x(bIdx)} y1="0" x2={x(bIdx)} y2={H} stroke="#f59e0b" strokeWidth="1.5" /> : null}
      {segs.map((s, i) => <polyline key={i} points={s.join(' ')} fill="none" stroke={stroke} strokeWidth="2" />)}
    </svg>
  );
}

/* ------------------------------------------------------------------- sayfa */

const RANGES = [{ d: 30, l: '30 gün' }, { d: 60, l: '60 gün' }, { d: 90, l: '90 gün' }];

export default function EuropcarCheckoutAudit() {
  const brandId = typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : null;
  const headers = useMemo(
    () => ({ 'Content-Type': 'application/json', ...(brandId ? { 'X-Brand-ID': brandId } : {}) }),
    [brandId]
  );

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(60);
  const [rt, setRt] = useState(null);
  const [polling, setPolling] = useState(false);
  const [auto, setAuto] = useState(true);
  const autoRef = useRef(auto);
  autoRef.current = auto;

  /** Hafif yoklama — tam denetimi yeniden çekmeden "şu an geliyor mu"yu tazeler. */
  const poll = useCallback(async () => {
    if (!brandId) return;
    setPolling(true);
    try {
      const res = await fetch(`${API_BASE}/api/europcar-ga4/checkout-audit?only=realtime`, {
        credentials: 'include', headers
      });
      const json = await res.json();
      if (res.ok && json.realtime) setRt(json.realtime);
    } catch { /* yoklama sessizce geçer; sayfanın geri kalanı ayakta kalsın */ }
    finally { setPolling(false); }
  }, [brandId, headers]);

  const load = useCallback(async (refresh) => {
    if (!brandId) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/europcar-ga4/checkout-audit?days=${days}${refresh ? '&refresh=1' : ''}`, {
        credentials: 'include', headers
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Denetim yüklenemedi');
      setData(json);
      // Önbellekli cevapta realtime bilerek yoktur; o zaman hafif yoklamayı
      // hemen tetikle ki "şu anda geliyor mu" satırı boş kalmasın.
      if (json.realtime) setRt(json.realtime); else poll();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [brandId, headers, days, poll]);

  useEffect(() => { load(false); }, [load]);

  useEffect(() => {
    const id = setInterval(() => { if (autoRef.current) poll(); }, 60000);
    return () => clearInterval(id);
  }, [poll]);

  const d = data;
  const dates = d?.dates || [];
  const spec = d?.spec || [];
  const fields = d?.detail?.fields || [];
  const daily = d?.detail?.daily || [];
  const spikeDates = d?.verdict?.spikeDates || [];

  const kayiplar = fields.filter((f) => f.brokeOn);
  const kazanclar = fields.filter((f) => f.startedOn);
  const gelmeyen = spec.filter((s) => s.durum === 'yok');
  const kesilen = spec.filter((s) => s.durum === 'kesildi');
  const bosItem = (d?.itemFields || []).filter((f) => f.share === 0);

  if (!brandId) {
    return <div className="mx-auto max-w-[900px] px-4 py-10 text-[14px] text-slate-600">Önce bir marka seçin.</div>;
  }

  return (
    <div lang="tr" className="mx-auto max-w-[1360px] px-4 py-6">

      {/* --------------------------------------------------------- başlık */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-[12px] font-bold uppercase tracking-wider text-emerald-700">Europcar · Checkout Etiketleme Denetimi</h1>
          <p className="mt-1.5 text-[26px] font-bold leading-tight tracking-tight text-slate-900">
            İstediğimiz dataLayer geldi mi?
          </p>
          <p className="mt-1.5 max-w-[92ch] text-[13px] leading-relaxed text-slate-500">
            Tamamı canlı GA4 — günlük tablolar, saat kırılımı ve gerçek zamanlı rapor.
            Ölçüt tek: <strong className="font-semibold text-slate-700">şu anda geliyor mu.</strong> Bir olayın
            geçmişte bir kez tetiklenmiş olması "uygulandı" demek değildir.
            {d?.meta ? ` Property ${d.meta.propertyId} · ${d.meta.range.start} → ${d.meta.range.end}.` : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex border border-slate-300">
            {RANGES.map((r) => (
              <button
                key={r.d} onClick={() => setDays(r.d)}
                className={`px-3 py-1.5 text-[12px] font-semibold ${days === r.d ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
              >{r.l}</button>
            ))}
          </div>
          <button
            onClick={() => load(true)} disabled={loading}
            className="inline-flex items-center gap-1.5 border border-slate-300 bg-white px-3 py-1.5 text-[12px] font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />Yeniden sor
          </button>
        </div>
      </div>

      {d?.meta?.failed?.length ? (
        <div className="mt-4 flex items-start gap-2 border border-amber-300 bg-amber-50 px-4 py-3 text-[13px] text-amber-900">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span>Bazı GA4 raporları alınamadı ({d.meta.failed.map((f) => f.reports.join(', ')).join(' · ')}). Eksik bölümler sıfır görünebilir.</span>
        </div>
      ) : null}

      {!d && loading ? <div className="mt-8 text-[14px] text-slate-500">GA4'e soruluyor…</div> : null}

      {d ? (
        <>
          <VerdictCard verdict={d.verdict} realtime={rt} today={d.today} />

          {/* ----------------------------------------------- anlık yoklama */}
          <Section
            icon={Radio} eyebrow="Anlık kontrol"
            title="Şu anda GA4'e ne düşüyor"
            note="Gerçek zamanlı rapor son 30 dakikayı gösterir ve önbelleğe alınmaz. Aranan olay listesi sabittir: dataLayer spesifikasyonunda istediklerimiz. Boş satır da bir cevaptır."
          >
            <LiveProbe realtime={rt} onPoll={poll} polling={polling} auto={auto} setAuto={setAuto} />
            <div className="mt-3 border border-slate-200 bg-white px-4 py-3">
              <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Bugün (gün henüz kapanmadı)</div>
              <div className="mt-1.5 text-[13px] leading-relaxed text-slate-600">
                {(d.today || []).length
                  ? (d.today || []).map((r) => `${r.name} ${nf(r.count)}`).join(' · ')
                  : 'Bugün için henüz olay yok.'}
              </div>
            </div>
          </Section>

          {/* ----------------------------------------- istenen ↔ gelen */}
          <Section
            icon={ListChecks} eyebrow="Talep ↔ gerçekleşen"
            title={`dataLayer spesifikasyonunda istenen ${spec.length} olayın durumu`}
            note="Talep tarafı 17 Ağustos 2026 tarihli belgeden birebir alınmıştır (bkz. dataLayer Specification sayfası). Durum sütunu canlı GA4'ten gelir: son 7 günde veri varsa CANLI, geçmişte gelip durmuşsa KESİLDİ, hiç gelmemişse YOK."
          >
            <div className="overflow-x-auto border border-slate-200 bg-white">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <Th>Olay</Th><Th>Öncelik</Th><Th>Belgedeki talep</Th><Th>Durum</Th>
                    <Th right>Toplam</Th><Th right>Aktif gün</Th><Th>İlk</Th><Th>Son</Th><Th right>Son 7 gün</Th>
                  </tr>
                </thead>
                <tbody>
                  {spec.map((r) => (
                    <tr key={r.name} className={r.durum === 'kesildi' ? 'bg-rose-50/50' : ''}>
                      <Td>
                        <div className="font-mono text-[12px] font-semibold text-slate-900">{r.name}</div>
                        <div className="text-[11px] text-slate-500">{r.label}</div>
                      </Td>
                      <Td>
                        <span className={`text-[11px] font-bold ${r.pri === 'P0' ? 'text-rose-700' : r.pri === 'P1' ? 'text-amber-700' : 'text-slate-500'}`}>{r.pri}</span>
                      </Td>
                      <Td><Badge kind={r.spec === 'DÜZELTME' ? 'uyari' : 'bilgi'}>{r.spec}</Badge></Td>
                      <Td>
                        <Badge kind={r.durum}>
                          {r.durum === 'canli' ? 'CANLI' : r.durum === 'kesildi' ? 'KESİLDİ' : 'YOK'}
                        </Badge>
                      </Td>
                      <Td right mono>{r.total ? nf(r.total) : '—'}</Td>
                      <Td right mono>{r.activeDays || '—'}</Td>
                      <Td mono>{dLabel(r.firstDate)}</Td>
                      <Td mono>{dLabel(r.lastDate)}</Td>
                      <Td right mono className={r.last7 ? 'font-semibold text-emerald-700' : 'text-slate-400'}>{r.last7 ? nf(r.last7) : '0'}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="border border-rose-200 bg-rose-50 px-4 py-3">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-rose-700">Hiç gelmeyen</div>
                <div className="mt-0.5 text-[22px] font-bold text-rose-900">{gelmeyen.length}</div>
                <div className="mt-1 font-mono text-[11px] leading-relaxed text-rose-700">{gelmeyen.map((s) => s.name).join(' · ') || '—'}</div>
              </div>
              <div className="border border-amber-200 bg-amber-50 px-4 py-3">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-amber-800">Gelip kesilen</div>
                <div className="mt-0.5 text-[22px] font-bold text-amber-900">{kesilen.length}</div>
                <div className="mt-1 font-mono text-[11px] leading-relaxed text-amber-800">{kesilen.map((s) => s.name).join(' · ') || '—'}</div>
              </div>
              <div className="border border-emerald-200 bg-emerald-50 px-4 py-3">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-emerald-700">Canlı</div>
                <div className="mt-0.5 text-[22px] font-bold text-emerald-900">{spec.filter((s) => s.durum === 'canli').length}</div>
                <div className="mt-1 font-mono text-[11px] leading-relaxed text-emerald-700">{spec.filter((s) => s.durum === 'canli').map((s) => s.name).join(' · ') || '—'}</div>
              </div>
            </div>
          </Section>

          {/* ------------------------------------------------- günlük seri */}
          <Section
            icon={Activity} eyebrow="Süreklilik"
            title={`Huni olayları · son ${days} gün, gün gün`}
            note="Her satır kendi içinde ölçeklenir; amaç büyüklük kıyaslamak değil, hangi olayın hangi gün geldiğini göstermek. Turuncu sütunlar checkout olaylarının görüldüğü günler."
          >
            <HeatStrip
              dates={dates}
              series={{ ...(d.series || {}), ...(d.extraSeries || {}) }}
              names={[...Object.keys(d.series || {}), ...Object.keys(d.extraSeries || {})]}
              highlight={spikeDates}
            />
            <p className="mt-2 text-[12px] leading-relaxed text-slate-500">
              <code className="font-mono">gtm.dom</code>, <code className="font-mono">form_start</code> ve <code className="font-mono">form_submit</code> referans
              olarak eklendi: bunlar GTM konteynerindeki yeni yapının yan ürünü, checkout olaylarıyla aynı gün açılıp aynı gün kapandılar.
              Yani olay tek tek değil, konteyner sürümü birlikte inip kalkmış.
            </p>
          </Section>

          {/* --------------------------------------------- saat kırılımı */}
          {d.hourly ? (
            <Section
              icon={Clock} eyebrow="Kanıt"
              title={`${dLong(d.hourly.date)} — checkout olaylarının görüldüğü tek gün, saat saat`}
              note="Olaylar gün boyu değil, belirli bir pencerede geldi. Bu bir yayın denemesinin imzasıdır: sürüm açılmış, birkaç saat canlı kalmış, geri alınmış. Gerçek bir yayında seri ertesi gün de devam ederdi."
            >
              <HourlyChart hourly={d.hourly} />
            </Section>
          ) : null}

          {/* --------------------------------- sipariş detayı: kazanç/kayıp */}
          <Section
            icon={Database} eyebrow="Sipariş detayı"
            title="Aynı sürüm: üç alan açıldı, üç alan kapandı"
            note="Checkout olayları yayında kalmadı ama purchase olayının içeriği 1 Eylül'de gerçekten değişti. Değişim iki yönlü: bazı alanlar ilk kez dolmaya başladı, bazıları o gün tamamen sustu. Grafiklerde turuncu dikey çizgi kırılma günü."
          >
            <div className="overflow-x-auto border border-slate-200 bg-white">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <Th>Alan</Th><Th>Belgede</Th><Th>Yön</Th><Th right>Önce</Th><Th right>Son 7 gün</Th><Th>Kırılma</Th><Th>Gün gün doluluk</Th>
                  </tr>
                </thead>
                <tbody>
                  {fields.map((f) => {
                    const kayip = !!f.brokeOn;
                    return (
                      <tr key={f.key} className={kayip ? 'bg-rose-50/40' : 'bg-emerald-50/30'}>
                        <Td>
                          <div className="flex items-center gap-1.5">
                            {kayip ? <ArrowDownRight size={14} className="text-rose-600" /> : <ArrowUpRight size={14} className="text-emerald-600" />}
                            <span className="font-semibold text-slate-900">{f.label}</span>
                          </div>
                          <div className="font-mono text-[11px] text-slate-500">{f.dim}</div>
                        </Td>
                        <Td><span className="font-mono text-[11px] text-slate-600">{f.spec}</span></Td>
                        <Td>
                          <Badge kind={kayip ? 'kesildi' : 'canli'}>{kayip ? 'KAYIP' : 'KAZANÇ'}</Badge>
                        </Td>
                        <Td right mono className="text-slate-500">{pct(f.was)}</Td>
                        <Td right mono className={`font-bold ${kayip ? 'text-rose-700' : 'text-emerald-700'}`}>{pct(f.now)}</Td>
                        <Td mono>{dLabel(f.brokeOn || f.startedOn)}</Td>
                        <Td className="w-[38%] min-w-[260px]">
                          <FillChart daily={daily} col={f.col} tone={kayip ? 'kayip' : 'kazanc'} breakDate={f.brokeOn || f.startedOn} />
                        </Td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
              <div className="border border-rose-200 bg-white">
                <div className="border-b border-rose-200 bg-rose-50 px-4 py-2 text-[12px] font-bold uppercase tracking-wider text-rose-800">
                  Kaybedilen {kayiplar.length} alan — bugün raporlanamıyor
                </div>
                <div className="px-4 py-3 text-[13px] leading-relaxed text-slate-600">
                  {kayiplar.length ? (
                    <>
                      <span className="font-mono text-[12px] text-slate-800">{kayiplar.map((f) => f.dim.replace('customEvent:', '')).join(' · ')}</span>
                      <p className="mt-2">
                        Bu üç parametre GA4'te kayıtlı özel boyut olduğu için {dLabel(kayiplar[0].brokeOn)} öncesine kadar
                        %100 doluydu. Parametreler artık hiçbir olayda gelmiyor — başka bir isim altına da taşınmamışlar.
                        Doğrudan sonucu: <strong className="font-semibold text-slate-800">GA4 panosundaki istasyon tablosu,
                        tek yön payı ve araç grubu tablosu Eylül için boş.</strong> Hata vermeden, sessizce.
                      </p>
                    </>
                  ) : 'Kayıp yok.'}
                </div>
              </div>
              <div className="border border-emerald-200 bg-white">
                <div className="border-b border-emerald-200 bg-emerald-50 px-4 py-2 text-[12px] font-bold uppercase tracking-wider text-emerald-800">
                  Kazanılan {kazanclar.length} alan — ilk kez ölçülebiliyor
                </div>
                <div className="px-4 py-3 text-[13px] leading-relaxed text-slate-600">
                  {kazanclar.length ? (
                    <>
                      <span className="font-mono text-[12px] text-slate-800">{kazanclar.map((f) => f.dim).join(' · ')}</span>
                      <p className="mt-2">
                        Araç markası, ödeme planı ve indirim tutarı {dLabel(kazanclar[0].startedOn)}'den beri geliyor.
                        Ödeme planı belgede <code className="font-mono">rate_plan</code> adıyla istenmişti; standart
                        <code className="mx-1 font-mono">item_variant</code> alanı üzerinden karşılanmış. Bu üçü
                        kayıtlı özel boyut gerektirmiyor, bugün raporlanabilir durumda.
                      </p>
                    </>
                  ) : 'Kazanç yok.'}
                </div>
              </div>
            </div>
          </Section>

          {/* --------------------------------- yeni açılan kırılımlar */}
          {(d.variant || []).length || (d.brands || []).length ? (
            <Section
              icon={CheckCircle2} eyebrow="Yeni açılan kırılım"
              title="Son 14 günde ölçülebilen iki yeni kırılım"
              note="Bu iki tablo 1 Eylül öncesinde kurulamıyordu. Ödeme planı kırılımı fiyatlama tarafının uzun süredir istediği veriydi."
            >
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <div className="border border-slate-200 bg-white">
                  <div className="border-b border-slate-200 px-4 py-2 text-[12px] font-bold uppercase tracking-wider text-slate-600">
                    Ödeme planı · <span className="font-mono normal-case">item_variant</span>
                  </div>
                  <table className="w-full border-collapse">
                    <thead><tr><Th>Plan</Th><Th right>Araç</Th><Th right>Pay</Th><Th right>Ciro</Th></tr></thead>
                    <tbody>
                      {(() => {
                        const tot = (d.variant || []).reduce((a, x) => a + x.units, 0) || 1;
                        return (d.variant || []).map((x) => (
                          <tr key={x.name}>
                            <Td><span className="font-mono text-[12px]">{x.name}</span></Td>
                            <Td right mono>{nf(x.units)}</Td>
                            <Td right mono className="font-semibold">{pct(x.units / tot, 1)}</Td>
                            <Td right mono>{tlShort(x.revenue)}</Td>
                          </tr>
                        ));
                      })()}
                    </tbody>
                  </table>
                </div>
                <div className="border border-slate-200 bg-white">
                  <div className="flex items-center justify-between border-b border-slate-200 px-4 py-2">
                    <span className="text-[12px] font-bold uppercase tracking-wider text-slate-600">
                      Araç markası · <span className="font-mono normal-case">item_brand</span>
                    </span>
                    <span className="text-[11px] text-slate-400">{(d.brands || []).length} farklı değer</span>
                  </div>
                  <table className="w-full border-collapse">
                    <thead><tr><Th>Marka</Th><Th right>Araç</Th><Th right>Ciro</Th></tr></thead>
                    <tbody>
                      {(d.brands || []).slice(0, 10).map((x) => (
                        <tr key={x.name}>
                          <Td>{x.name}</Td>
                          <Td right mono>{nf(x.units)}</Td>
                          <Td right mono>{tlShort(x.revenue)}</Td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="border-t border-slate-100 px-4 py-2.5 text-[12px] leading-relaxed text-amber-800">
                    <AlertTriangle size={12} className="mr-1 inline" />
                    Marka değerleri tek biçim değil — <span className="font-mono">Skoda/SKODA</span>,{' '}
                    <span className="font-mono">Toyota/TOYOTA</span>, <span className="font-mono">VW/Volkswagen/VOLKSWAGEN</span> ayrı
                    satır sayılıyor. Düzeltilmezse marka bazlı rapor bölünür.
                  </div>
                </div>
              </div>
            </Section>
          ) : null}

          {/* --------------------------------------- hâlâ boş olan alanlar */}
          <Section
            icon={MinusCircle} eyebrow="Kapanmayan boşluk"
            title="Belgede istenen ama hâlâ hiç gelmeyen alanlar"
            note="Son 14 günün tamamında boş. Bunlar 'ölçülemez' değil — sitede karşılığı var, GA4'e gönderilmiyor."
          >
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div className="border border-slate-200 bg-white">
                <table className="w-full border-collapse">
                  <thead><tr><Th>Item alanı</Th><Th>Neden isteniyor</Th></tr></thead>
                  <tbody>
                    {bosItem.map((f) => (
                      <tr key={f.dim}>
                        <Td><span className="font-mono text-[12px] text-slate-800">{f.dim}</span><div className="text-[11px] text-slate-500">{f.label}</div></Td>
                        <Td className="text-[12px] text-slate-500">{f.why}</Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="border border-slate-200 bg-white">
                <div className="border-b border-slate-200 px-4 py-2 text-[12px] font-bold uppercase tracking-wider text-slate-600">Tutar alanları · son {days} gün</div>
                <table className="w-full border-collapse">
                  <tbody>
                    <tr><Td>KDV (<span className="font-mono text-[11px]">tax</span>)</Td><Td right mono className={d.ecom.tax ? '' : 'text-rose-700 font-semibold'}>{d.ecom.tax ? tl(d.ecom.tax) : 'hiç gönderilmiyor'}</Td></tr>
                    <tr><Td>İade (<span className="font-mono text-[11px]">refund</span>)</Td><Td right mono className={d.ecom.refund ? '' : 'text-rose-700 font-semibold'}>{d.ecom.refund ? tl(d.ecom.refund) : 'hiç gönderilmiyor'}</Td></tr>
                    <tr><Td>İndirim (<span className="font-mono text-[11px]">discount</span>)</Td><Td right mono className="font-semibold text-emerald-700">{d.ecom.itemDiscount ? tl(d.ecom.itemDiscount) : 'yok'}</Td></tr>
                  </tbody>
                </table>
                <div className="px-4 py-2.5 text-[12px] leading-relaxed text-slate-500">
                  İade gönderilmediği için elimizdeki ciro <strong className="font-semibold text-slate-700">brüt</strong>;
                  net rezervasyon cirosu GA4'ten okunamıyor. KDV alanı da boş olduğu için mevcut tutarın KDV dahil mi
                  hariç mi olduğu belirsiz.
                </div>
              </div>
            </div>
          </Section>

          {/* ------------------------------------------------ kayıt defteri */}
          <Section
            icon={Database} eyebrow="GA4 tarafı"
            title="Kayıtlı özel boyut ve metrikler"
            note="Bu bizim tarafımızdaki iş. Site yeni bir parametre göndermeye başlasa bile GA4'te kayıtlı değilse raporlanamaz — Data API kayıtlı olmayan bir customEvent boyutunu hata ile reddeder. Aşağıdaki liste bugünkü kayıt defteri."
          >
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
              <div className="border border-slate-200 bg-white">
                <div className="border-b border-slate-200 px-4 py-2 text-[12px] font-bold uppercase tracking-wider text-slate-600">Özel boyutlar ({d.registry.dimensions.length})</div>
                <table className="w-full border-collapse">
                  <tbody>
                    {d.registry.dimensions.map((x) => {
                      const olu = fields.some((f) => f.dim === `customEvent:${x.param}` && f.brokeOn);
                      return (
                        <tr key={x.param}>
                          <Td><span className="font-mono text-[12px]">{x.param}</span></Td>
                          <Td right>{olu ? <Badge kind="kesildi">veri gelmiyor</Badge> : null}</Td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="border border-slate-200 bg-white">
                <div className="border-b border-slate-200 px-4 py-2 text-[12px] font-bold uppercase tracking-wider text-slate-600">Özel metrikler ({d.registry.metrics.length})</div>
                <table className="w-full border-collapse">
                  <tbody>
                    {d.registry.metrics.map((x) => (
                      <tr key={x.param}><Td><span className="font-mono text-[12px]">{x.param}</span></Td><Td right><Badge kind="canli">çalışıyor</Badge></Td></tr>
                    ))}
                  </tbody>
                </table>
                <div className="px-4 py-2.5 text-[12px] leading-relaxed text-slate-500">
                  <code className="font-mono">rental_days</code> 1 Eylül sürümünden etkilenmemiş; kiralama süresi
                  kesintisiz geliyor.
                </div>
              </div>
              <div className="border border-slate-200 bg-white">
                <div className="border-b border-slate-200 px-4 py-2 text-[12px] font-bold uppercase tracking-wider text-slate-600">Veri akışları</div>
                <table className="w-full border-collapse">
                  <tbody>
                    {d.registry.streams.map((x) => (
                      <tr key={x.measurementId}>
                        <Td><div className="text-[12px]">{x.name}</div><div className="font-mono text-[11px] text-slate-500">{x.measurementId}</div></Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="px-4 py-2.5 text-[12px] leading-relaxed text-slate-500">
                  Olay verisi saklama süresi <strong className="font-semibold text-slate-700">{d.meta.retention === 'TWO_MONTHS' ? '2 ay' : d.meta.retention}</strong> ve
                  BigQuery bağlantısı yok. Bugün ölçülmeyen şey kalıcı olarak kaybolur; geriye dönük kurtarma yok.
                </div>
              </div>
            </div>
          </Section>

          {/* ------------------------------------------- web ekibine maddeler */}
          <Section
            icon={Send} eyebrow="Karşı tarafa"
            title="Europcar web ekibine iletilecek maddeler"
            note="Hepsi bu sayfadaki canlı ölçümden türedi; sırası öncelik sırasıdır."
          >
            <div className="border border-slate-200 bg-white">
              {[
                {
                  n: 1, t: `Checkout olayları neden ${dLabel(spikeDates[0])} akşamı durdu?`,
                  d: `${kesilen.map((s) => s.name).join(', ')} olayları ${dLong(spikeDates[0])} günü yaklaşık 11:00–18:00 arasında site genelinde tetiklendi, sonra kesildi. GTM konteyner sürümü geri mi alındı, yoksa yayın yalnızca bir ortamda mı kaldı? Kalıcı yayına alınması gereken sürüm bu.`,
                  tone: 'rose'
                },
                {
                  n: 2, t: 'pickup_city, dropoff_city ve car_type parametreleri geri gelmeli',
                  d: `Aynı sürümle birlikte bu üç parametre purchase olayından düştü ve ${dLabel(kayiplar[0]?.brokeOn)} tarihinden beri hiçbir olayda gelmiyor. Bunlar GA4'te kayıtlı özel boyut; ofis ve araç grubu bazlı tüm raporlama bunlara bağlı. Yeni yapıya taşınırken atlanmış görünüyor.`,
                  tone: 'rose'
                },
                {
                  n: 3, t: 'Hiç tetiklenmemiş P0 olayları',
                  d: `${gelmeyen.filter((s) => s.pri === 'P0').map((s) => s.name).join(', ')} — belgede huninin ölçülebilmesi için zorunlu (P0) işaretliydi, bugüne kadar bir kez bile gelmedi. Bunlar olmadan sepet terk noktası ve karşılanamayan talep ölçülemez.`,
                  tone: 'amber'
                },
                {
                  n: 4, t: 'view_item_list hâlâ items[] olmadan gidiyor',
                  d: 'Olay günlük olarak tetikleniyor ama ürün dizisi boş; listelenen araçlar ve liste adı bilinmiyor. Belgedeki düzeltme maddesi henüz uygulanmamış.',
                  tone: 'amber'
                },
                {
                  n: 5, t: 'tax ve refund alanları purchase içine eklenmeli',
                  d: 'İade gönderilmediği için ciro brüt kalıyor, KDV alanı boş olduğu için tutarın KDV dahil mi olduğu belirsiz. İndirim alanı 1 Eylül\'de eklenmiş; aynı yerde bu ikisi de eklenebilir.',
                  tone: 'slate'
                },
                {
                  n: 6, t: 'Marka değerleri tek biçime getirilmeli',
                  d: 'item_brand alanında aynı marka farklı yazımlarla geliyor (Skoda/SKODA, Toyota/TOYOTA, VW/Volkswagen/VOLKSWAGEN). Kaynakta normalize edilmezse marka bazlı raporlar bölünür.',
                  tone: 'slate'
                }
              ].map((m) => (
                <div key={m.n} className="flex gap-3 border-b border-slate-100 px-4 py-3 last:border-b-0">
                  <div className={`flex h-6 w-6 shrink-0 items-center justify-center text-[12px] font-bold text-white ${m.tone === 'rose' ? 'bg-rose-600' : m.tone === 'amber' ? 'bg-amber-500' : 'bg-slate-400'}`}>{m.n}</div>
                  <div>
                    <div className="text-[14px] font-semibold text-slate-900">{m.t}</div>
                    <div className="mt-1 max-w-[100ch] text-[13px] leading-relaxed text-slate-600">{m.d}</div>
                  </div>
                </div>
              ))}
            </div>
          </Section>

          <p className="mt-10 border-t border-slate-200 pt-4 text-[12px] leading-relaxed text-slate-400">
            Tüm rakamlar GA4 property {d.meta.propertyId} üzerinden canlı çekilir; hiçbir veri ambarına bakılmaz.
            Tam denetim 5 dakika önbelleklenir, gerçek zamanlı bölüm önbelleklenmez.
            Son çekim {saatFark(d.meta.generatedAt)} · {d.meta.elapsedMs} ms{d.meta.cached ? ' · önbellekten' : ''}.
          </p>
        </>
      ) : null}
    </div>
  );
}
